import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type {
  Message,
  MessagesResponse,
  Trip,
  TripResponse,
  PusherInstance,
  PusherChannel
} from '@this/components/trips_management/trips/trips_chat/types';
import type { RouteComponentProps } from 'react-router-dom';
import { Link } from '@this/shared/ui/navigations/link';

import TripChatHeader from '@this/components/trips_management/trips/trips_chat/trip_chat_header';
import TripChatForm from '@this/components/trips_management/trips/trips_chat/trip_chat_form';
import { Fetcher, HTTPError, isAiTravel, appendScript, getParams } from '@this/src/util';
import Notification from '../../../../notification';
import TripChatInformation from './trip_chat_information';
import TripChatMessage from './trip_chat_message';
import SlackIntegrationAlert from './slack_integration_alert';
import AiArrangementBanner from './ai_chat/AiArrangementBanner';

type AiArrangementConfirmation = {
  id: number;
  content: string;
  summary: {
    items: {
      category: string;
      label: string;
      text: string;
      price: number | null;
      price_with_tax: number | null;
    }[];
    total_price: number;
    total_price_with_tax: number;
    has_selection: boolean;
  } | null;
} | null;

interface Props extends RouteComponentProps {
  serviceId: number;
}

const TripsChat: React.FC<Props> = ({ serviceId, history }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const [trip, setTrip] = useState<Trip | undefined>(undefined);
  const [messages, setMessages] = useState<Message[]>([]);
  const [firstTrip, setFirstTrip] = useState(false);
  const [windowFocused] = useState(true); // 将来的にwindow focus/blur eventで使用予定
  const [businessHoursMessage, setBusinessHoursMessage] = useState<string | undefined>(undefined);
  const [enabledChat, setEnabledChat] = useState(false);
  const [aiArrangementConfirmation, setAiArrangementConfirmation] = useState<AiArrangementConfirmation>(null);

  const fetchMessageOnceRef = useRef(false);
  const scrollMessageRef = useRef<HTMLParagraphElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const initPropsRef = useRef<TripResponse | undefined>(undefined);
  const pusherRef = useRef<PusherInstance | undefined>(undefined);
  const channelRef = useRef<PusherChannel | undefined>(undefined);
  const messagesRef = useRef<Message[]>([]);

  const handleScroll = useCallback((e: JQuery.ScrollEvent) => {
    const messageWindow = e.target;
    if (
      scrollMessageRef.current &&
      messageWindow.scrollTop === messageWindow.scrollHeight - messageWindow.clientHeight
    ) {
      $(scrollMessageRef.current).hide(300);
    }
  }, []);

  const notifyReadMessage = useCallback(() => {
    const partnerMessages: Message[] = [];
    messages.forEach(m => {
      if (m.type === 'partner') {
        partnerMessages.push(m);
      }
    });

    const partnerMessageIds = partnerMessages.map(m => m.id);
    const params = {
      partner_message_ids: partnerMessageIds
    };
    Fetcher.post('/trips/messages/read.json', params).then(() => {
      window.dispatchEvent(new CustomEvent('unreadMessagesUpdated'));
    });
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    if (messageContainerRef.current) {
      const node = messageContainerRef.current;
      node.scrollTop = node.scrollHeight;
    }
  }, []);

  const timeCheckerRef = useRef<NodeJS.Timeout | null>(null);

  const startTimeChecker = useCallback(async () => {
    const result = await Fetcher.get<{ message: string }>('/business_hours_messages.json');
    setBusinessHoursMessage(result.message);
    timeCheckerRef.current = setTimeout(startTimeChecker, 1000 * 60);
  }, []);

  const showEnabledChatAlert = useCallback(
    async (tripId: string) => {
      Notification.error('旅程が承認されていないため\nチャットをご利用いただけません。\n詳細画面へ移動しました。');
      history.push(`/trips/${tripId}`);
    },
    [history]
  );

  const handleCloseFlash = useCallback(() => {
    setFirstTrip(false);
  }, []);

  const fetchMessages = useCallback(async () => {
    const tripId = trip ? trip.id : initPropsRef.current?.trip.id;
    if (!tripId) return;

    try {
      const result = await Fetcher.get<MessagesResponse>(`/trips/${tripId}/messages.json`, {
        type: 'user'
      });
      const oldMessageCount = messagesRef.current.length;

      // スクロールするか否かの判断はajaxの結果をもって判断できるが、
      // スクロールアクション自体はビューが更新された後に行う
      let doScroll = false;

      const messageWindow = messageContainerRef.current;

      // 現在のスクロール位置が一番下にある時スクロールする
      if (messageWindow && messageWindow.scrollHeight === messageWindow.scrollTop + messageWindow.offsetHeight) {
        doScroll = true;
      } else {
        if (scrollMessageRef.current && oldMessageCount < result.messages.length) {
          // 新しいメッセージがあったら
          // スクロールしないが、新着のメッセージがある場合は
          // 「新しいメッセージをみるにはスクロールダウンして下さい」というメッセージを表示する
          $(scrollMessageRef.current).show(300);
        }

        // 初回描画時のみ
        if (!fetchMessageOnceRef.current) {
          fetchMessageOnceRef.current = true;
          // 確認画面から遷移があった時、それが初めての遷移であればスクロールしない
          if (document.referrer !== `${location.origin}/reserve_confirm` || oldMessageCount !== 1) {
            doScroll = true;
          }
        }
      }

      setTrip(result.trip);
      setMessages(result.messages);
      messagesRef.current = result.messages;

      if (doScroll) {
        scrollToBottom();
      }
    } catch (e) {
      Notification.error('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
    }
  }, [trip, scrollToBottom]);

  const handleMessageSubmit = useCallback(
    async (text: string, files: File[], e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();

      if (!trip) return;

      const formData = new FormData();
      if (files.length) {
        files.forEach(file => {
          formData.append('files[]', file);
        });
      }
      formData.append('message', text);
      formData.append('type', 'user');

      try {
        await Promise.all([
          Fetcher.upload(`/trips/${trip.id}/messages`, formData).catch(error => {
            if (error instanceof HTTPError && error.response?.data.errors) {
              Notification.error(`送信に失敗しました。${error.response.data.errors}`);
            }
          }),
          Fetcher.put(`/trips/${trip.id}/order_item_snoozed_todos/cancel`, {})
        ]);
        await fetchMessages();
      } catch {
        Notification.error('送信に失敗しました');
      }
    },
    [trip, fetchMessages]
  );

  const subscribeTrip = useCallback(() => {
    const tripId = trip ? trip.id : initPropsRef.current?.trip.id;
    if (pusherRef.current && initPropsRef.current && tripId) {
      channelRef.current = pusherRef.current.subscribe(
        initPropsRef.current.pusherChannelPrefix + tripId.toString()
      );
      channelRef.current.bind('new_message', fetchMessages);
    }
  }, [trip, fetchMessages]);

  const websocketTimerRef = useRef<NodeJS.Timeout | null>(null);

  const initWebsocket = useCallback(() => {
    if (typeof Pusher !== 'undefined') {
      if (initPropsRef.current) {
        pusherRef.current = new Pusher(initPropsRef.current.pusherKey, { encrypted: true });
        if (pusherRef.current?.connection) {
          pusherRef.current.connection.bind('connected', subscribeTrip);
        }
      }
    } else {
      websocketTimerRef.current = setTimeout(initWebsocket, 100);
    }
  }, [subscribeTrip]);

  useEffect(() => {
    appendScript('https://js.pusher.com/3.0/pusher.min.js');
    startTimeChecker();

    const splitPath = location.href.split('/');
    const tripId = splitPath.find(s => s.match(/^\d+$/)) || '';

    const initializeChat = async () => {
      try {
        const result = await Fetcher.get<TripResponse>(`/trips/${tripId}.json`, getParams());

        if (!result.enabledChat) {
          await showEnabledChatAlert(tripId);
          return;
        }

        initPropsRef.current = result;
        initWebsocket();

        setTrip(result.trip);
        setMessages(result.messages);
        setFirstTrip(!!result.firstTrip);
        setEnabledChat(result.enabledChat);

        const confirmationResult = await Fetcher.get<{ arrangement_confirmation: AiArrangementConfirmation }>(
          `/trips/${tripId}/ai_arrangement_confirmation.json`
        );
        setAiArrangementConfirmation(confirmationResult.arrangement_confirmation);

        // 初回メッセージ取得
        await fetchMessages();
      } catch (e: unknown) {
        if (typeof e === 'object' && e !== null && 'response' in e) {
          const error = e as { response?: { status?: number } };
          if (error.response?.status === 404) {
            Notification.error('このページの閲覧権限がありません。');
          }
        }
      }
    };

    initializeChat();

    // クリーンアップ関数
    return () => {
      if (timeCheckerRef.current) {
        clearTimeout(timeCheckerRef.current);
      }
      if (websocketTimerRef.current) {
        clearTimeout(websocketTimerRef.current);
      }
      if (channelRef.current) {
        channelRef.current.unbind('new_message');
      }
      if (pusherRef.current && initPropsRef.current?.trip.id) {
        const currentTripId = initPropsRef.current.trip.id;
        pusherRef.current.unsubscribe(initPropsRef.current.pusherChannelPrefix + currentTripId.toString());
        pusherRef.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 初回マウント時のみ実行

  useEffect(() => {
    const currentRef = messageContainerRef.current;
    if (currentRef) {
      $(currentRef).on('scroll', handleScroll);
    }

    return () => {
      if (currentRef) {
        $(currentRef).off('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    if (windowFocused && messages.length > 0) {
      notifyReadMessage();
    }
  }, [messages, windowFocused, notifyReadMessage]);

  return (
    <div style={styles.container} className="trips-chat__container">
      <TripChatHeader />
      <SlackIntegrationAlert />
      <div style={styles.body}>
        <p ref={scrollMessageRef} style={styles.scrollMessage}>
          新しいメッセージを見るにはスクロールダウンして下さい
        </p>
        <div ref={messageContainerRef} style={styles.messageContainer}>
          <TripChatInformation shown={firstTrip} handleClose={handleCloseFlash}>
            旅程管理に移動しました
          </TripChatInformation>
          {aiArrangementConfirmation && trip && (
            <AiArrangementBanner tripId={trip.id} summary={aiArrangementConfirmation.summary} />
          )}
          {messages.map(m => (
            <TripChatMessage key={m.id} message={m} onSubmitConfirmation={fetchMessages} />
          ))}
        </div>
        {enabledChat && <TripChatForm handleSubmit={handleMessageSubmit} />}
        {businessHoursMessage && (
          <p style={styles.businessHourMessage}>
            <span>※現在は営業時間外です。</span>
            <span data-wovn-ignore>{businessHoursMessage}</span>
            <span>以降にご返信いたします。</span>
          </p>
        )}
        {isAiTravel(serviceId) && (
          <Link href="/knowledge_categories/1" rel="noopener noreferrer" target="_blank">
            よくあるご質問
          </Link>
        )}
        {isAiTravel(serviceId) && (
          <Link href="/knowledges/000001075" rel="noopener noreferrer" target="_blank">
            弊社営業時間外または出張当日の変更・キャンセルの対応方法について
          </Link>
        )}
      </div>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    background: theme.contentBgColor,
    width: 1150,
    padding: '0 20px 20px',
    margin: '0 auto',
    flexGrow: 1
  } as const,
  body: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    justifyContent: 'space-between' as const,
    height: 'calc(100vh - 200px)'
  } as const,
  messageContainer: {
    flex: 1,
    paddingTop: 10,
    marginBottom: 10,
    overflowX: 'hidden' as const,
    overflowY: 'scroll' as const
  } as const,
  scrollMessage: {
    fontSize: 12,
    padding: 3,
    color: '#7d7d7d',
    display: 'none' as const
  } as const,
  businessHourMessage: {
    fontSize: 12,
    color: '#7d7d7d'
  } as const
});

export default TripsChat;
